// widget/src/hooks/useChat.ts
import { useState, useCallback, useRef } from 'react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface UseChatOptions {
  supabaseUrl: string;
  supabaseAnonKey: string;
  userEmail: string | null;
}

export interface UseChatReturn {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
}

export function useChat({ supabaseUrl, supabaseAnonKey, userEmail }: UseChatOptions): UseChatReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasInitiated = useRef(false);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    // Guard against multiple 'Hi' calls (common in StrictMode)
    if (content === 'Hi') {
      if (hasInitiated.current || messages.length > 0) return;
      hasInitiated.current = true;
    }

    const userMessage: Message = { role: 'user', content };

    // Add user message immediately (but don't show "Hi" as first message)
    if (content !== 'Hi') {
      setMessages(prev => [...prev, userMessage]);
    }

    setIsLoading(true);
    setError(null);

    try {
      // Build conversation history for API
      const conversationMessages = content === 'Hi'
        ? [{ role: 'user' as const, content: 'Hi, I just arrived on the website and want to learn more.' }]
        : [...messages, userMessage].map(m => ({ role: m.role, content: m.content }));

      const response = await fetch(`${supabaseUrl}/functions/v1/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          messages: conversationMessages,
          userEmail,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const assistantMessage: Message = {
        role: 'assistant',
        content: data.message,
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (err) {
      console.error('Chat error:', err);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [messages, supabaseUrl, supabaseAnonKey, userEmail]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
  };
}

export default useChat;
